// Streaming parser for the AI Builder marker protocol.
//
// Wire format (one continuous text stream):
//   <<<KIND:html>>>\n
//   <<<TITLE>>>Some title<<<END>>>\n
//   <<<FILE:index.html>>>\n
//   ...file content...\n
//   <<<END>>>\n
//   (more <<<FILE:path>>>...<<<END>>> blocks for multi-file)
//
// We feed chunks into a stateful parser that emits incremental updates so
// the UI can render file content live.

export type BuildKind = "html" | "react";

export type BuildState = {
  kind: BuildKind | null;
  title: string;
  files: { path: string; content: string }[];
  activePath: string | null; // the file currently being streamed into
  raw: string;
};

export const newBuildState = (): BuildState => ({
  kind: null,
  title: "",
  files: [],
  activePath: null,
  raw: "",
});

const KIND_RE = /<<<KIND:(html|react)>>>/;
const TITLE_RE = /<<<TITLE>>>([\s\S]*?)<<<END>>>/;
const FILE_OPEN_RE = /<<<FILE:([^>]+)>>>\n?/;
const FILE_CLOSE = "<<<END>>>";

export function feedBuildStream(state: BuildState, chunk: string): BuildState {
  state.raw += chunk;

  // Pull KIND and TITLE if not yet captured.
  if (!state.kind) {
    const km = state.raw.match(KIND_RE);
    if (km) state.kind = km[1] as BuildKind;
  }
  if (!state.title) {
    const tm = state.raw.match(TITLE_RE);
    if (tm) state.title = tm[1].trim();
  }

  // Walk through any complete FILE blocks plus an in-progress one.
  // We parse from the beginning every time using a simple state machine.
  const files: { path: string; content: string }[] = [];
  let activePath: string | null = null;
  let i = 0;
  const text = state.raw;
  while (i < text.length) {
    const open = text.indexOf("<<<FILE:", i);
    if (open === -1) break;
    const headerEnd = text.indexOf(">>>", open);
    if (headerEnd === -1) break;
    const path = text.slice(open + 8, headerEnd);
    let bodyStart = headerEnd + 3;
    if (text[bodyStart] === "\n") bodyStart += 1;
    const close = text.indexOf(FILE_CLOSE, bodyStart);
    if (close === -1) {
      // partial — last open file is in-progress
      activePath = path;
      files.push({ path, content: text.slice(bodyStart) });
      break;
    }
    let content = text.slice(bodyStart, close);
    if (content.endsWith("\n")) content = content.slice(0, -1);
    files.push({ path, content });
    i = close + FILE_CLOSE.length;
  }

  state.files = files;
  state.activePath = activePath;
  return state;
}

export function inferLanguage(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "ts":
    case "tsx":
      return "typescript";
    case "js":
    case "jsx":
      return "javascript";
    case "html":
      return "html";
    case "css":
      return "css";
    case "json":
      return "json";
    case "md":
      return "markdown";
    default:
      return "plaintext";
  }
}